import { isAxiosError } from 'axios'
import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { errorMessage } from './errorMessage'
import {
  PhotoUploadError,
  type UploadedPhoto,
  usePhotoService,
} from './photoService'
import { photoUploadStore } from './photoUploadStore'
import type { PhotoElection, PhotoSource } from './types'

/**
 * Lifecycle of one queued photo: `uploading` (the upload protocol), then
 * `attaching` (the surface's commit), then gone from the queue; or failed
 * at either step with retry/discard, where retry resumes at the failed step.
 */
export type PhotoUploadPhase =
  | 'uploading'
  | 'attaching'
  | 'attached'
  | 'removing'
  | 'upload_failed'
  | 'attach_failed'

export type CommitResult = 'attached' | 'already_attached'

type Commit = (photo: UploadedPhoto) => Promise<CommitResult>

type Remove = (photo: UploadedPhoto) => Promise<void>

export interface PhotoUploadItem {
  key: string
  siteId: string
  subjectUid: string
  file: File
  previewUrl: string
  photoId: string | null
  elections: PhotoElection[]
  phase: PhotoUploadPhase
  error: string | null
  retryable: boolean
  /** Bound when the file was added, so a retry lands on the same subject. */
  commit: Commit
  remove: Remove
  onSettled: () => void
  onDuplicate: () => void
}

/** Below this the VLM read is not trusted over the subject-name fallback. */
const LABEL_CONFIDENCE_MIN = 0.7

const FORBIDDEN_MESSAGE =
  'You do not have permission to add photos on this site.'

const isImageFile = (file: File): boolean => file.type.startsWith('image/')

const isForbidden = (error: unknown): boolean => {
  const cause = error instanceof PhotoUploadError ? error.cause : error
  return isAxiosError(cause) && cause.response?.status === 403
}

const failure = (
  error: unknown
): Pick<PhotoUploadItem, 'error' | 'retryable'> =>
  isForbidden(error)
    ? { error: FORBIDDEN_MESSAGE, retryable: false }
    : { error: errorMessage(error), retryable: true }

export function useQueuedPhotoIds(
  siteId: string,
  subjectUid: string
): ReadonlySet<string> {
  const allItems = useSyncExternalStore(
    photoUploadStore.subscribe,
    photoUploadStore.getItems
  )
  return useMemo(
    () =>
      new Set(
        allItems.flatMap((item) =>
          item.siteId === siteId &&
          item.subjectUid === subjectUid &&
          item.photoId
            ? [item.photoId]
            : []
        )
      ),
    [allItems, siteId, subjectUid]
  )
}

/**
 * Uploads each file, commits it through the surface's `commit` as soon as it
 * lands, then refines the election label from the VLM read in the background.
 * `onSettled` fires after every write so the surface can refresh. Items live
 * in `photoUploadStore`, so they outlive the surface that queued them; this
 * hook sees only the ones queued for its subject.
 */
export function usePhotoUploadQueue(
  siteId: string,
  subjectUid: string,
  source: PhotoSource,
  commit: Commit,
  onSettled: () => void,
  {
    retainAttached = false,
    onDuplicate = () => undefined,
    remove: removeOption,
  }: {
    retainAttached?: boolean
    onDuplicate?: () => void
    remove?: Remove
  } = {}
) {
  const photoService = usePhotoService()
  const remove = useCallback<Remove>(
    (photo) =>
      removeOption
        ? removeOption(photo)
        : photoService.discardPhoto(photo.photoId),
    [photoService, removeOption]
  )
  const allItems = useSyncExternalStore(
    photoUploadStore.subscribe,
    photoUploadStore.getItems
  )
  const items = useMemo(
    () =>
      allItems.filter(
        (item) => item.siteId === siteId && item.subjectUid === subjectUid
      ),
    [allItems, siteId, subjectUid]
  )

  const refineLabel = useCallback(
    async (item: PhotoUploadItem, photoId: string) => {
      const read = await photoService.generatePhotoLabel(photoId)
      if (!read) return
      if (read.confidence !== null && read.confidence < LABEL_CONFIDENCE_MIN) {
        return
      }
      await photoService.setPhotoLabel(photoId, read.label)
      item.onSettled()
    },
    [photoService]
  )

  const runCommit = useCallback(
    async (item: PhotoUploadItem, photo: UploadedPhoto) => {
      photoUploadStore.patch(item.key, { phase: 'attaching', error: null })
      let result: CommitResult
      try {
        result = await item.commit(photo)
      } catch (error) {
        photoUploadStore.patch(item.key, {
          phase: 'attach_failed',
          ...failure(error),
        })
        return
      }
      if (result === 'already_attached') {
        photoUploadStore.remove(item.key)
        item.onDuplicate()
        return
      }
      if (retainAttached) {
        photoUploadStore.patch(item.key, { phase: 'attached' })
      } else {
        photoUploadStore.remove(item.key)
      }
      item.onSettled()
      refineLabel(item, photo.photoId).catch(() => undefined)
    },
    [refineLabel, retainAttached]
  )

  const runUpload = useCallback(
    async (item: PhotoUploadItem) => {
      photoUploadStore.patch(item.key, { phase: 'uploading', error: null })
      let photo: UploadedPhoto
      try {
        if (item.photoId) {
          await photoService.retryPhotoUpload(item.photoId, item.file)
          photo = { photoId: item.photoId, elections: item.elections }
        } else {
          photo = await photoService.uploadPhoto(siteId, source, item.file)
        }
      } catch (error) {
        photoUploadStore.patch(item.key, {
          photoId:
            error instanceof PhotoUploadError
              ? error.photoEntryId
              : item.photoId,
          phase: 'upload_failed',
          ...failure(error),
        })
        return
      }
      const alreadyQueued = photoUploadStore
        .getItems()
        .some(
          (other) =>
            other.key !== item.key &&
            other.siteId === item.siteId &&
            other.subjectUid === item.subjectUid &&
            other.photoId === photo.photoId
        )
      if (alreadyQueued) {
        photoUploadStore.remove(item.key)
        item.onDuplicate()
        return
      }
      photoUploadStore.patch(item.key, photo)
      await runCommit(item, photo)
    },
    [photoService, runCommit, siteId, source]
  )

  const addFiles = useCallback(
    (files: File[]) => {
      const newItems = files.filter(isImageFile).map(
        (file): PhotoUploadItem => ({
          key: uuidv4(),
          siteId,
          subjectUid,
          file,
          previewUrl: URL.createObjectURL(file),
          photoId: null,
          elections: [],
          phase: 'uploading',
          error: null,
          retryable: true,
          commit,
          remove,
          onSettled,
          onDuplicate,
        })
      )
      if (newItems.length === 0) return
      photoUploadStore.add(newItems)
      newItems.forEach((item) => void runUpload(item))
    },
    [commit, onDuplicate, onSettled, remove, runUpload, siteId, subjectUid]
  )

  const retryItem = useCallback(
    (key: string) => {
      const item = photoUploadStore.find(key)
      if (!item?.retryable) return
      if (item.phase === 'attach_failed' && item.photoId) {
        void runCommit(item, {
          photoId: item.photoId,
          elections: item.elections,
        })
      } else if (item.phase === 'upload_failed') {
        void runUpload(item)
      }
    },
    [runCommit, runUpload]
  )

  /** A failed soft delete only leaves a harmless husk, so it is not surfaced. */
  const discardItem = useCallback(
    (key: string) => {
      const item = photoUploadStore.find(key)
      if (item?.photoId) {
        photoService.discardPhoto(item.photoId).catch(() => undefined)
      }
      photoUploadStore.remove(key)
    },
    [photoService]
  )

  const removeAttachedItem = useCallback(async (key: string) => {
    const item = photoUploadStore.find(key)
    if (item?.phase !== 'attached' || !item.photoId) return
    photoUploadStore.patch(key, { phase: 'removing', error: null })
    try {
      await item.remove({ photoId: item.photoId, elections: item.elections })
    } catch (error) {
      photoUploadStore.patch(key, {
        phase: 'attached',
        error: errorMessage(error),
      })
      return
    }
    photoUploadStore.remove(key)
    item.onSettled()
  }, [])

  return useMemo(
    () => ({ items, addFiles, retryItem, discardItem, removeAttachedItem }),
    [addFiles, discardItem, items, removeAttachedItem, retryItem]
  )
}
